import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getImageUrlWithEnv } from '../utils/imageUrl';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  CalendarIcon,
  ClockIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import { addActivity } from '../services/activityService';
import apiService from '../services/apiService';

const MatchManagement = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const matchesData = await apiService.getMatches();
      setMatches(matchesData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading matches:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const match = matches.find(item => item.id === id);
    if (window.confirm('ნამდვილად გსურთ ამ მატჩის წაშლა?')) {
      try {
        await apiService.deleteMatch(id);
        if (match) {
          addActivity('match', 'deleted', {
            title: `${match.homeTeam.name} vs ${match.awayTeam.name}`,
            description: `მატჩი "${match.homeTeam.name} vs ${match.awayTeam.name}" წაიშალა`
          });
        }
        loadMatches();
      } catch (error) {
        console.error('Error deleting match:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ka-GE');
  };

  const getMatchTypeLabel = (type) => {
    return type === 'upcoming' ? 'შემდეგი მატჩი' : 'წინა მატჩი';
  };

  const getMatchTypeColor = (type) => {
    return type === 'upcoming' ? 'text-blue-600' : 'text-green-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader 
        title="მატჩების მართვა"
        subtitle="მართეთ შემდეგი და წინა მატჩები"
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/matches/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          ახალი მატჩი
        </button>
      </AdminHeader>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {matches.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 font-georgian">მატჩები არ არის</h3>
              <p className="mt-1 text-sm text-gray-500 font-georgian">დაიწყეთ ახალი მატჩის დამატებით</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/_panel-7c39f8/matches/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 font-georgian"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  ახალი მატჩი
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      ტიპი
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      გუნდები
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      თარიღი
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      დრო
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      სტადიონი
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      ტურნირი
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      ქულა
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider font-georgian">
                      მოქმედებები
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {matches.map((match) => (
                    <tr key={match.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMatchTypeColor(match.type)}`}>
                          {getMatchTypeLabel(match.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <img className="h-8 w-8 rounded-full" src={getImageUrlWithEnv(match.homeTeam.logo)} alt={match.homeTeam.name} />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 font-georgian">
                              {match.homeTeam.name} vs {match.awayTeam.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian">
                        {formatDate(match.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian">
                        {match.time || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian">
                        {match.venue || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian">
                        {match.tournament || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-georgian">
                        {match.score || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => navigate(`/_panel-7c39f8/matches/${match.id}/edit`)}
                            className="text-primary-600 hover:text-primary-900 font-georgian"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(match.id)}
                            className="text-red-600 hover:text-red-900 font-georgian"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchManagement;
